/**
 * SiteList — E-1. 현장 목록
 *
 * SitesListPage 본문 — 검색 영역 카드 + 현장 카드 그리드(4열).
 * 레이아웃은 나의 사진첩 현장 목록, 카드 구성은 Figma 카드 예시(4366:2817)를 따른다.
 * 키워드 검색 · 기간 · 상태 · 담당자·관리자(D-3) → GET /customer/sites
 * 연필 버튼 → 현장 상세/편집(onEditSite)
 * 휴지통 버튼 → PATCH /customer/sites/{siteId}/status { siteStatus: 'DONE' }
 */
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button, Badge, DateRangePicker, EmptyState, FilterChip, Input, SearchCombo, Skeleton, Tag, TextLink, Tooltip,
} from '@indipia/ui-core';
import { getSiteColor, useAppStore, useDateRangeFilter } from '@indipia/shared';
import { useCompanyMembers, useDefaultMemberFilter, useMemberFilterState } from '@indipia/shared-features';
import type { CompanyMember } from '@indipia/shared-features';
import {
  fetchSiteList,
  fetchSiteActivityAgents,
} from '../../shared/api/sites';
import type { SiteListItem, SiteListParams } from '../../shared/api/sites';
import type { WorkspaceTab } from './SiteWorkspaceDrawer';
import { SignalDot, type SignalColor } from '../../shared/todo/SignalDot';
import { useSiteSignals } from '../../shared/todo/useSiteSignals';
import { useDebouncedValue } from '../../shared/hooks/useDebouncedValue';
import {
  InfoList, InfoRow, ListCard, ListCardActions, ListCardGrid, ListCardHeader, ListCardIdentity,
} from '../../shared/components/ListCard';

// 현장 카드의 작업물 탭 — 활동 유무와 무관하게 항상 노출하고, 활동이 없으면 비활성만
// 시킨다(설계서 v2.0 FR-14, 기획서 E.나의 현장). 셋 다 SiteWorkspaceDrawer를 그 탭으로 열어
// 현장 스코프를 유지한다 — 예전엔 AGENT_MENU_CONFIG.defaultPath로 전체 목록 페이지에
// navigate()해 현장 정보가 날아갔다.
// 2026-10-06 개선사항 B — 시공사례 제거, 공사스케줄→사진첩→나의 할일 순으로 재정렬.
// agentId=null(나의 할일)은 아직 실제 기능이 없어 활동 유무 체크 대상이 아니다 — 현장이
// 완료(DONE) 상태가 아니면 항상 활성(클릭하면 플레이스홀더 화면으로 이동, 사용자 확인 완료).
// 2026-10-16 940 S0-4(6) — 버튼 4개, 순서 나의 할일·진행현황(거래완료=완료현황)·사진첩·공사현황.
// 데이터 없음 모양(회색 글자+점선 밑줄)은 클릭해도 열리지 않고 호버 안내만 띄운다.
// 나의 할일(agentId=null)의 데이터 유무는 현장 신호 API(useSiteSignals — 빨강·노랑 건수), 공사현황은 agentId 2.
const WORKSPACE_TAGS: {
  agentId: number | null; label: string; tab: WorkspaceTab; emptyTip: string; doneEmptyTip: string;
}[] = [
  { agentId: null, label: '나의 할일', tab: 'todo',
    emptyTip: '이 현장에 지금 처리할 일이 없어요.',
    doneEmptyTip: '거래완료된 현장이라 처리할 일이 없어요.' },
  { agentId: 8, label: '진행현황', tab: 'schedule',
    emptyTip: '공사스케줄이 아직 없어요. 스튜디오홈에서 스케줄 생성을 실행하면 진행현황이 보여요.',
    doneEmptyTip: '이 현장은 공사스케줄 없이 거래완료됐어요.' },
  { agentId: 1, label: '사진첩', tab: 'photos',
    emptyTip: '사진이 아직 없어요. 스튜디오홈에서 사진정리를 실행하면 현장별로 사진을 정리할 수 있어요.',
    doneEmptyTip: '이 현장은 사진 없이 거래완료됐어요.' },
  { agentId: 2, label: '공사현황', tab: 'work',
    emptyTip: '작업요청·작업보고가 아직 없어요. 스튜디오홈 할일에서 작업요청을 보내면 여기에 쌓여요.',
    doneEmptyTip: '이 현장은 작업요청·작업보고 없이 거래완료됐어요.' },
];

// ── 상수 ────────────────────────────────────────────────────────────────────

/** 현장 대표색(서버) → 신호 점 색. 모든 공정이 흰색·빗금이면 서버가 WHITE(시작 전)를 준다. 없음(null)은 점을 그리지 않는다. */
const SITE_DOT_COLOR: Partial<Record<string, SignalColor>> = {
  RED: 'red', YELLOW: 'yellow', GREEN: 'green', BLUE: 'blue', WHITE: 'white',
};

/** 신호 점 색 → 마우스를 올렸을 때 이름(범례와 같은 말). */
const SITE_DOT_TITLE: Record<SignalColor, string> = {
  red: '조치 필요', yellow: '대기', green: '정상', blue: '완료', white: '시작 전',
};

// appStore init 완료 전 fallback
const FALLBACK_SITE_STATUS = [
  { codeValue: 'ACTIVE',   codeName: '등록' },
  { codeValue: 'PROGRESS', codeName: '진행중' },
  { codeValue: 'DONE',     codeName: '거래완료' },
];

// ── 헬퍼 ────────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

// 현장 상태 → ui-core Badge status 매핑 (DQ-C03)
function siteBadgeStatus(status: string): 'success' | 'warning' | 'error' | 'info' {
  switch (status) {
    case 'ACTIVE':   return 'info';
    case 'PROGRESS': return 'warning';
    case 'DONE':     return 'success';
    default:         return 'error';
  }
}

// 도면 없음/도면 완료 배지 — 버전2 대상, 임시 주석처리 (noUnusedLocals 방지 위해 헬퍼 함수도 함께 주석처리)
// function planStatusLabel(status: string): string {
//   if (status === 'READY') return '도면 완료';
//   return '도면 없음';
// }

// function planStatusCls(status: string): string {
//   if (status === 'READY') return 'text-success';
//   return 'text-text-muted';
// }

// ── 하위 컴포넌트 ────────────────────────────────────────────────────────────

/**
 * 담당자·관리자 필터 본문(펼친 상태) — 2026-10-12 개선사항 D-3. 플랫폼 E. 나의 현장만
 * '칩 + [+ 추가] + [나로 초기화]' 방식(D.0 — 화면별로 통일하지 않음).
 *  - 선택 직원 칩(× 로 제외, 이름만). 모두 제외하면 전체 현장
 *  - [+ 추가] → 같은 자리에 이름 검색칸. 검색 결과 1건이면 자동 강조 · Enter 로 추가(검색어 비움),
 *    ↑↓ 로 고른 항목은 검색어가 없어도 Enter 로 추가, Esc · 바깥 클릭 닫기
 *  - 기본값과 다르면 [나로 초기화](사장님은 [전체로 초기화])
 */
function MemberFilterBody({
  members, values, onChange, myId, isOwner, defaultIds,
}: {
  members: CompanyMember[];
  values: number[];
  onChange: (v: number[]) => void;
  myId: number | null;
  isOwner: boolean;
  defaultIds: number[];
}) {
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(-1);
  const q = query.trim();
  const nameOf = (id: number) => members.find((m) => m.memberId === id)?.memberName ?? `회원 ${id}`;
  const candidates = members.filter(
    (m) => !values.includes(m.memberId) && (!q || m.memberName.includes(q)),
  );
  // ↑↓ 로 고른 항목이 우선, 없으면 검색 결과 1건일 때 자동 강조
  const highlighted = active >= 0 ? active : (q && candidates.length === 1 ? 0 : -1);
  const isDefault = values.length === defaultIds.length && values.every((v) => defaultIds.includes(v));

  function add(id: number) {
    onChange([...values, id]);
    setQuery('');
    setActive(-1);
  }

  function close() {
    setAdding(false);
    setQuery('');
    setActive(-1);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.nativeEvent.isComposing) return;   // 한글 조합 중 Enter·화살표 무시
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, candidates.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlighted >= 0 && candidates[highlighted]) add(candidates[highlighted].memberId);
    } else if (e.key === 'Escape') { close(); }
  }

  return (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-sm">
        {values.map((id) => (
          <Tag key={id} size="sm" onRemove={() => onChange(values.filter((v) => v !== id))}>
            {nameOf(id)}
          </Tag>
        ))}
        {adding ? (
          <div className="menu-anchor">
            <Input
              className="field-auto"
              size="sm"
              autoFocus
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActive(-1); }}
              onKeyDown={onKeyDown}
              onBlur={close}
              placeholder="직원 이름 검색"
              aria-label="담당자·관리자 추가"
            />
            <div className="menu show" role="listbox">
              {candidates.length === 0 ? (
                <div className="combo-empty">추가할 직원이 없습니다</div>
              ) : candidates.map((m, i) => (
                <div
                  key={m.memberId}
                  role="option"
                  aria-selected={i === highlighted}
                  className={`menu-item ${i === highlighted ? 'active' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => add(m.memberId)}
                  onMouseEnter={() => setActive(i)}
                >
                  <span className="truncate">{m.memberName}{m.memberId === myId ? ' (나)' : ''}</span>
                  {m.roleCode === 'OWNER' && <Badge status="warning" className="ml-auto">사장님</Badge>}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <Button variant="etc" size="sm" onClick={() => setAdding(true)}>
            + 추가
          </Button>
        )}
        {!isDefault && (
          <TextLink className="ml-auto text-caption" onClick={() => onChange(defaultIds)}>
            {isOwner ? '전체로 초기화' : '나로 초기화'}
          </TextLink>
        )}
      </div>
    </div>
  );
}


// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────

function memberNames(members?: { memberName: string | null }[]): string {
  const names = (members ?? []).map((m) => m.memberName).filter(Boolean);
  return names.length ? names.join(', ') : '-';
}

function IcMapPin() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  );
}

export function SiteList({
  onSelectSite,
  onEditSite,
  onAddSite,
  signalOn = false,
}: {
  /** 신호등 켜기 — 카드에 현장 신호 점·나의 할일 숫자 표시(실데이터: 현장 신호 API). */
  signalOn?: boolean;
  // item까지 같이 넘겨준다 — 현장 작업물 슬라이드오버 헤더에 siteName을 바로 쓰기 위해
  // (siteId만으로는 호출부가 또 조회해야 함). initialTab은 태그 클릭 시 그 탭으로 바로 열기
  // 위한 것 — 카드 자체 클릭이면 undefined(기본 탭).
  onSelectSite?: (siteId: number, item: SiteListItem, initialTab?: WorkspaceTab) => void;
  onEditSite?: (siteId: number) => void;
  onAddSite?: () => void;
}) {
  // 검색 — 입력 디바운스로 실시간 조회, 버튼/Enter는 즉시
  const [searchInput,  setSearchInput]  = useState('');
  const [keyword,      setKeyword]      = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 300);
  useEffect(() => { setKeyword(debouncedSearch.trim()); }, [debouncedSearch]);

  // 기간 — 기본 미지정(전체)
  const { dateRange, setDateRange, dateFrom, dateTo, reset: resetDateRange } = useDateRangeFilter();

  // 상태
  const [statusFilter, setStatusFilter] = useState<string | null>(null); // null = 전체

  // 담당자·관리자(D-3) — 기본값: 직원은 나, 사장님은 조건 없음(전체 현장)
  const { myId, isOwner, defaultIds } = useDefaultMemberFilter();
  const { data: companyMembers = [] } = useCompanyMembers();
  const [memberIds,  setMemberIds]  = useMemberFilterState();

  // ZEU_SITE_STATUS 공통코드 — appStore init 완료 전에는 fallback 사용
  const allCodes = useAppStore((s) => s.codes);
  const siteStatusCodes = (() => {
    const filtered = allCodes
      .filter((c) => c.codeGroupCode === 'ZEU_SITE_STATUS')
      .sort((a, b) => a.codeOrder - b.codeOrder);
    return filtered.length > 0 ? filtered : FALLBACK_SITE_STATUS;
  })();

  const params: SiteListParams = {
    sort: 'last_access',
    // last_access 정렬은 회사 전체 최근활동 상위 limit건을 먼저 뽑은 뒤 필터링하는 방식이라
    // 기본값(20)이면 필터·검색 결과가 실제보다 적게 보일 수 있다 — 넉넉하게 100으로 올려둔다.
    limit: 100,
    ...(statusFilter ? { siteStatus: statusFilter } : {}),
    ...(keyword  ? { keyword }  : {}),
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo   ? { dateTo }   : {}),
    ...(memberIds.length ? { memberIds } : {}),
  };
  const hasActiveFilters = !!statusFilter || !!keyword || !!dateFrom || !!dateTo || memberIds.length > 0;

  function clearFilters() {
    // 결과 없음 화면의 초기화 — 담당자·관리자 조건까지 풀어 전체 현장을 보여준다
    // (기본값 [나]로 되돌리면 담당 현장이 없는 직원은 같은 빈 화면에 머문다)
    setMemberIds([]);
    setStatusFilter(null);
    setKeyword('');
    setSearchInput('');
    resetDateRange();
  }

  const { data, isLoading } = useQuery({
    queryKey: ['site-list', params],
    queryFn:  () => fetchSiteList(params),
  });

  // 필터와 무관한 전체 현장 개수 — "진짜 0개"와 "필터 결과 0개"를 구분하기 위한 용도
  // 'site-list' 접두어를 공유해 현장 등록/변경 시 invalidateQueries(['site-list'])로 같이 무효화되게 함
  const { data: unfilteredData } = useQuery({
    queryKey: ['site-list', 'total'],
    queryFn:  () => fetchSiteList({}),
    enabled:  hasActiveFilters,
    staleTime: 60_000,
  });
  // unfilteredData 로딩 중(undefined)엔 "진짜 0개"로 성급히 단정하지 않고 필터 결과 없음 쪽으로 폴백
  const isTrulyEmpty = !hasActiveFilters || (unfilteredData?.total ?? -1) === 0;

  const items = data?.items ?? [];
  const siteIds = items.map((item) => item.siteId);

  // 현재 로드된 현장들의 에이전트 태그 — 스튜디오 홈 우측 현장 목록 전용 조회
  const { data: activityAgentsData } = useQuery({
    queryKey: ['site-activity-agents', siteIds],
    queryFn:  () => fetchSiteActivityAgents(siteIds),
    enabled:  siteIds.length > 0,
  });
  const agentTagsBySiteId = new Map<number, number[]>(
    (activityAgentsData ?? []).map((a) => [a.siteId, a.agentIds]),
  );

  // 현장별 신호(대표색·빨강/노랑 건수) — 나의 할일 버튼의 데이터 유무 판정에도 쓰므로 신호등 토글과 무관하게 조회한다.
  // 조회 오류는 폴백 없이 오류로 둔다(신호등을 켠 경우 카드에 '신호 오류' 표시, 나의 할일 버튼은 막지 않음).
  const { data: siteSignals, isLoading: sigLoading, isError: sigError } = useSiteSignals({
    siteIds, enabled: siteIds.length > 0,
  });
  const signalBySiteId = new Map((siteSignals ?? []).map((s) => [s.siteId, s]));



  return (
    <div className="flex flex-col gap-5">

      {/* 검색 영역 — 나의 사진첩 현장 목록과 같은 카드 묶음 (①키워드·기간 ②상태 ③담당자·관리자) */}
      <div className="flex flex-col gap-2.5 rounded-xl border border-border bg-surface-raised p-5">
        <div className="flex flex-wrap items-center gap-2">
          <SearchCombo
            className="min-w-60 flex-1"
            value={searchInput}
            onChange={(v) => setSearchInput(v.slice(0, 50))}
            onSearch={() => setKeyword(searchInput.trim())}
            onClear={() => { setSearchInput(''); setKeyword(''); }}
            maxLength={50}
            placeholder="현장명 · 주소 · 고객명 검색"
            aria-label="현장 검색"
          />
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            placeholder="기간 전체"
            aria-label="기간"
          />
          {hasActiveFilters && (
            <Button type="button" variant="etc" onClick={clearFilters} className="shrink-0">
              초기화
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span className="w-24 shrink-0 text-caption font-semibold text-text-secondary">상태</span>
          <FilterChip active={statusFilter === null} onClick={() => setStatusFilter(null)}>
            전체
          </FilterChip>
          {siteStatusCodes.map((code) => (
            <FilterChip
              key={code.codeValue}
              active={statusFilter === code.codeValue}
              onClick={() => setStatusFilter(code.codeValue)}
            >
              {code.codeName}
            </FilterChip>
          ))}
        </div>

        {/* 담당자·관리자 — 2026-10-12 개선사항 D-3 */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="w-24 shrink-0 text-caption font-semibold text-text-secondary">담당자·관리자</span>
          <MemberFilterBody
            members={companyMembers}
            values={memberIds}
            onChange={setMemberIds}
            myId={myId}
            isOwner={isOwner}
            defaultIds={defaultIds}
          />
        </div>
      </div>

      {/* 현장 카드 그리드 — 4열 고정 */}
      {isLoading ? (
        <ListCardGrid>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-72 rounded-xl" />)}
        </ListCardGrid>
      ) : items.length === 0 && !isTrulyEmpty ? (
        <EmptyState
          icon={<IcMapPin />}
          title="조회 결과가 없습니다"
          description="검색어·기간·상태·담당자 조건에 맞는 현장이 없어요. 다른 조건으로 다시 검색해 보세요."
          action={<Button type="button" variant="secondary" onClick={clearFilters}>필터 초기화</Button>}
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<IcMapPin />}
          title="등록된 현장이 없습니다"
          description="첫 현장을 등록하고 고객·현장을 관리해 보세요."
          action={<Button type="button" variant="primary" onClick={onAddSite}>+ 첫 현장 등록</Button>}
        />
      ) : (
        <ListCardGrid>
          {items.map((item) => {
            const isDone = item.siteStatus === 'DONE';
            // 거래완료 현장은 신호등(점 · 신호 오류)을 보이지 않는다 — 나의 할일 버튼 숫자도 아래 hasData 에서 빠진다.
            const showSignal = signalOn && !isDone;
            const sigCode = signalBySiteId.get(item.siteId)?.representativeColorCode;
            const sigColor = showSignal && sigCode ? SITE_DOT_COLOR[sigCode] : undefined;
            const badges = [
              // 상태 뱃지 — ui-core Badge (DQ-C03)
              <Badge key="status" status={siteBadgeStatus(item.siteStatus)}>{item.siteStatusName ?? item.siteStatus}</Badge>,
              showSignal && sigError && (
                <span key="sigerr" title="현장 신호를 불러오지 못했어요" className="text-caption font-semibold text-error">신호 오류</span>
              ),
              sigColor && (
                <span key="sig" title={SITE_DOT_TITLE[sigColor]} className="inline-flex">
                  <SignalDot color={sigColor} size={12} />
                </span>
              ),
            ];
            return (
              // 카드 클릭은 마우스 편의용(role 없음) — 키보드·스크린리더는 현장명 버튼으로 연다(버튼 중첩 회피)
              <ListCard
                key={item.siteId}
                accentColor={item.siteColor ?? getSiteColor(item.siteId)}
                onClick={() => onSelectSite?.(item.siteId, item)}
                className="cursor-pointer transition-colors hover:border-point"
              >
                <ListCardHeader
                  badges={badges}
                  title={item.siteName}
                  editLabel="현장 상세/편집"
                  onEdit={() => onEditSite?.(item.siteId)}
                />

                <ListCardIdentity
                  name={item.siteName}
                  onNameClick={() => onSelectSite?.(item.siteId, item)}
                  phone={item.customerMobile}
                  lead={item.customerName}
                  detail={item.siteAddress}
                />

                <InfoList>
                  <InfoRow label="담당자">{memberNames(item.chargeMembers)}</InfoRow>
                  <InfoRow label="관리자">{memberNames(item.managerMembers)}</InfoRow>
                  <InfoRow label="등록일">{formatDate(item.createdAt)}</InfoRow>
                  <InfoRow label="최근활동">{item.lastAccessDtm ? formatDate(item.lastAccessDtm) : '-'}</InfoRow>
                </InfoList>

                {/* 작업물 버튼 4종(나의 할일·진행현황·사진첩·공사현황) — 활동 유무와 무관하게 항상 노출하고
                    데이터가 없으면 회색 + 호버 안내만(FR-14). 클릭 시 이 현장으로 스코프된 SiteWorkspaceDrawer를 그 탭으로 연다. */}
                <ListCardActions>
                  {WORKSPACE_TAGS.map(({ agentId, label, tab, emptyTip, doneEmptyTip }) => {
                    // 데이터 있음 판정 — 나의 할일은 현장 신호(빨강·노랑 1건 이상; 조회 중·오류면 막지 않음, 거래완료는 없음),
                    // 나머지는 활동로그 agentId 포함 여부.
                    const siteSig = signalBySiteId.get(item.siteId);
                    const hasData = agentId === null
                      ? (isDone ? false : sigLoading || sigError || (siteSig ? siteSig.redCount + siteSig.yellowCount > 0 : false))
                      : (agentTagsBySiteId.get(item.siteId) ?? []).includes(agentId);
                    // 진행현황 버튼만 거래완료면 '완료현황'.
                    const displayLabel = tab === 'schedule' && isDone ? '완료현황' : label;
                    // 나의 할일 버튼 안 빨강·노랑 숫자(신호등 켠 때, 0이면 생략)
                    const sig = tab === 'todo' && signalOn && siteSig ? { red: siteSig.redCount, yellow: siteSig.yellowCount } : null;
                    const inner = (
                      <>
                        {displayLabel}
                        {sig && hasData && sig.red > 0 && (
                          <span className="ml-1 inline-flex items-center gap-0.5">
                            <SignalDot color="red" size={8} />{sig.red}
                          </span>
                        )}
                        {sig && hasData && sig.yellow > 0 && (
                          <span className="ml-1 inline-flex items-center gap-0.5">
                            <SignalDot color="yellow" size={8} />{sig.yellow}
                          </span>
                        )}
                      </>
                    );
                    if (!hasData) {
                      // 없음: 열리지 않고 호버 안내만(aria-disabled — disabled 는 호버 이벤트가 불안정)
                      return (
                        <Tooltip key={tab} tip={isDone ? doneEmptyTip : emptyTip} className="w-full [&>.trigger]:w-full [&>.trigger]:border-0">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            aria-disabled="true"
                            onClick={(e) => e.stopPropagation()}
                            className="w-full cursor-help !border-border !bg-transparent !text-text-muted"
                          >
                            {inner}
                          </Button>
                        </Tooltip>
                      );
                    }
                    return (
                      <Button
                        key={tab}
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectSite?.(item.siteId, item, tab);
                        }}
                        className="w-full"
                      >
                        {inner}
                      </Button>
                    );
                  })}
                </ListCardActions>
              </ListCard>
            );
          })}
        </ListCardGrid>
      )}

    </div>
  );
}
